/**
 * Papierkorb und Rueckgaengig (F-45) fuer die App - Huellen um den Kern in
 * `db/papierkorb/papierkorb.ts` mit der App-Datenbank.
 *
 * Verwendung in einem Repository (die Huelle sitzt INNEN, nie um eine
 * UI-Abfrage herum):
 *
 *   export const deleteProject = (id: ID) =>
 *     inDenPapierkorb({ art: 'geloescht', tabelle: 'projects', zeileIds: [id] }, async () => { ... });
 */
import type { Table } from 'dexie';

import { db } from '@/db/schema';
import * as kern from '@/db/papierkorb/papierkorb';
import type { AktionsBeschreibung, WiederherstellErgebnis } from '@/db/papierkorb/papierkorb';
import type { Aenderung, Hinweis } from '@/domain/papierkorb/typen';

export type { AktionsBeschreibung, WiederherstellErgebnis } from '@/db/papierkorb/papierkorb';

/** Loeschaktion ausfuehren und als EINEN Papierkorb-Eintrag ablegen. */
export const inDenPapierkorb = <T>(b: AktionsBeschreibung, fn: () => Promise<T>): Promise<T> =>
  kern.inDenPapierkorb(db, b, fn);

/** Sonstige Aktion ausfuehren und rueckgaengig machbar melden (nur im Speicher). */
export const rueckgaengigMachbar = <T>(b: AktionsBeschreibung, fn: () => Promise<T>, tabellen?: Table[]): Promise<T> =>
  kern.rueckgaengigMachbar(db, b, fn, tabellen);

/** Eintrag wiederherstellen - `'unvollstaendig'`, solange der Sync noch Zeilen nachliefert. */
export const wiederherstellen = (eintragId: string): Promise<WiederherstellErgebnis | 'unvollstaendig' | null> =>
  kern.wiederherstellen(db, eintragId);

/** Eine Aktion aus dem Speicher zuruecknehmen (Rueckgaengig ohne Papierkorb-Eintrag). */
export const zuruecknehmen = (aenderungen: readonly Aenderung[]): Promise<{ zurueck: number; hinweise: Hinweis[] }> =>
  kern.zuruecknehmen(db, aenderungen);

export const endgueltigLoeschen = (eintragId: string): Promise<void> => kern.endgueltigLoeschen(db, eintragId);

export const papierkorbLeeren = (): Promise<void> => kern.papierkorbLeeren(db);

/** Beim Start und beim Oeffnen der Papierkorb-Seite: Abgelaufenes (30 Tage) entfernen. */
export const papierkorbAufraeumen = (jetzt = new Date()): Promise<number> => kern.aufraeumen(db, jetzt);

/** Titel fuer einen Eintrag: getrimmt, hoechstens 80 Zeichen (wie `eintragTitel`); leer = Standardtitel. */
export const papierkorbTitel = (text: string | null | undefined): string | undefined =>
  text?.trim().slice(0, 80) || undefined;
